import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tabs,
  Tab,
  Box,
  Paper,
  Stack,
  Divider,
  TableContainer,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatPrice } from '../../Formatter/formatPrice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../Redux/Store';
import {
  checkoutBillOrder,
  closeDiningTable,
} from '../../Redux/ActiveStore/activeStoreApi';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';

interface BillConfirmModalProps {
  billData: any;
  storeId: string;
  activeStoreId: string;
  activeDiningTableId: string;
  onClose: () => void;
  /** 顧客が会計リクエストで選んだ支払方法。状態があるときだけ見出し下に表示する（ADR 0068）。 */
  paymentMethod?: string | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bill-tabpanel-${index}`}
      aria-labelledby={`bill-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const BillConfirmModal: React.FC<BillConfirmModalProps> = ({
  billData,
  storeId,
  activeStoreId,
  activeDiningTableId,
  onClose,
  paymentMethod = null,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState(0);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [closeTableDialogOpen, setCloseTableDialogOpen] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleCheckoutOrder = async () => {
    try {
      setCheckoutDialogOpen(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleConfirmCheckout = async () => {
    try {
      await dispatch(
        checkoutBillOrder({ storeId, activeStoreId, activeDiningTableId })
      );
      onClose();
    } catch (error) {
      console.error('Failed to checkout order:', error);
    } finally {
      setCheckoutDialogOpen(false);
    }
  };

  const handleCloseDiningTable = async () => {
    try {
      setCloseTableDialogOpen(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleConfirmCloseTable = async () => {
    try {
      await dispatch(
        closeDiningTable({ storeId, activeStoreId, activeDiningTableId })
      );
      onClose();
    } catch (error) {
      console.error('Failed to close dining table:', error);
    } finally {
      setCloseTableDialogOpen(false);
    }
  };

  const calculateItemPrice = (basePrice: number, option: any) => {
    if (!option?.menuOptionPrice) return basePrice;
    return option.menuOptionPriceType === 'ADDITIONAL'
      ? basePrice + option.menuOptionPrice
      : basePrice - option.menuOptionPrice;
  };

  const groupItemsByMenu = (items: any[]) => {
    const groupedItems = new Map();
    
    items.forEach((item) => {
      const key = item.menuId;
      if (!groupedItems.has(key)) {
        groupedItems.set(key, {
          menuId: item.menuId,
          menuName: item.menuName,
          basePrice: item.menuPrice,
          options: [],
          totalQuantity: 0,
          totalPrice: 0
        });
      }
      const group = groupedItems.get(key);
      const itemPrice = calculateItemPrice(item.menuPrice, item.menuOption);
      group.options.push({
        cartItemId: item.cartItemId,
        option: item.menuOption,
        quantity: item.quantity,
        price: itemPrice,
        subTotalPrice: item.subTotalPrice
      });
      group.totalQuantity += item.quantity;
      group.totalPrice += item.subTotalPrice;
    });
    
    return Array.from(groupedItems.values());
  };

  const renderBillItems = () => (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
        <Stack spacing={1}>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            合計金額: {formatPrice(billData.byBillItems.totalPrice)}円
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            合計税額: {formatPrice(billData.byBillItems.totalTax)}円
          </Typography>
          <Typography variant="subtitle2" color="error" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
            会計金額: {formatPrice(billData.byBillItems.totalBillPrice)}円
          </Typography>
        </Stack>
      </Paper>

      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>メニュー名</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>価格</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>数量</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>小計</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupItemsByMenu(billData.byBillItems.items).map((group) => (
              <TableRow key={group.menuId} hover>
                <TableCell>
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        whiteSpace: 'pre-wrap', 
                        wordBreak: 'break-word',
                        fontSize: '0.875rem',
                        lineHeight: 1.2
                      }}
                    >
                      {group.menuName}
                    </Typography>
                    {group.options.map((item: any) => (
                      item.option && (
                        <Typography 
                          key={item.cartItemId}
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            whiteSpace: 'nowrap',
                            mt: 0.5,
                            fontSize: '0.75rem',
                            lineHeight: 1.2
                          }}
                        >
                          {item.option.menuOptionName}
                          {item.option.menuOptionPrice && (
                            item.option.menuOptionPriceType === 'ADDITIONAL' ? 
                            ` (+${formatPrice(item.option.menuOptionPrice)}円)` :
                            ` (-${formatPrice(item.option.menuOptionPrice)}円)`
                          )}
                        </Typography>
                      )
                    ))}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {formatPrice(group.basePrice)}円
                  </Typography>
                  {group.options.map((item: any) => (
                    item.option && (
                      <Typography 
                        key={item.cartItemId}
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          whiteSpace: 'nowrap',
                          mt: 0.5,
                          fontSize: '0.75rem',
                          lineHeight: 1.2
                        }}
                      >
                        {formatPrice(item.price)}円
                      </Typography>
                    )
                  ))}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {group.totalQuantity}
                  </Typography>
                  {group.options.map((item: any) => (
                    item.option && (
                      <Typography 
                        key={item.cartItemId}
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          whiteSpace: 'nowrap',
                          mt: 0.5,
                          fontSize: '0.75rem',
                          lineHeight: 1.2
                        }}
                      >
                        {item.quantity}
                      </Typography>
                    )
                  ))}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatPrice(group.totalPrice)}円</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );

  const renderCartItems = () => (
    <Stack spacing={3}>
      {billData.byCartItems.map((cart: any) => (
        <Paper key={cart.cartId} elevation={0} sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }} gutterBottom>
            注文日時: {new Date(cart.createdAt).toLocaleString()}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>メニュー名</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>価格</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>数量</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>小計</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupItemsByMenu(cart.items).map((group) => (
                  <TableRow key={group.menuId} hover>
                    <TableCell>
                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word',
                            fontSize: '0.875rem',
                            lineHeight: 1.2
                          }}
                        >
                          {group.menuName}
                        </Typography>
                        {group.options.map((item: any) => (
                          item.option && (
                            <Typography 
                              key={item.cartItemId}
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                whiteSpace: 'nowrap',
                                mt: 0.5,
                                fontSize: '0.75rem',
                                lineHeight: 1.2
                              }}
                            >
                              {item.option.menuOptionName}
                              {item.option.menuOptionPrice && (
                                item.option.menuOptionPriceType === 'ADDITIONAL' ? 
                                ` (+${formatPrice(item.option.menuOptionPrice)}円)` :
                                ` (-${formatPrice(item.option.menuOptionPrice)}円)`
                              )}
                            </Typography>
                          )
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatPrice(group.basePrice)}円
                      </Typography>
                      {group.options.map((item: any) => (
                        item.option && (
                          <Typography 
                            key={item.cartItemId}
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              whiteSpace: 'nowrap',
                              mt: 0.5,
                              fontSize: '0.75rem',
                              lineHeight: 1.2
                            }}
                          >
                            {formatPrice(item.price)}円
                          </Typography>
                        )
                      ))}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {group.totalQuantity}
                      </Typography>
                      {group.options.map((item: any) => (
                        item.option && (
                          <Typography 
                            key={item.cartItemId}
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              whiteSpace: 'nowrap',
                              mt: 0.5,
                              fontSize: '0.75rem',
                              lineHeight: 1.2
                            }}
                          >
                            {item.quantity}
                          </Typography>
                        )
                      ))}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatPrice(group.totalPrice)}円</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2} />
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    小計
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatPrice(cart.totalPrice)}円
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}
    </Stack>
  );

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          // 固定 430px はスマホ(390px)で画面からはみ出すため、sm 以上のみ最小幅を効かせる
          minWidth: { xs: 'auto', sm: '430px' },
          // 会計明細は情報量が多いため、スマホではほぼ横幅いっぱいに広げる（余白 8px）
          m: { xs: 1, sm: 4 },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          '& .MuiTableCell-root': {
            padding: '8px',
            fontSize: '0.875rem',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6">会計確認</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 3 }}>
        {paymentMethod && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            希望お支払い方法: {paymentMethod}
          </Typography>
        )}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="bill tabs"
            variant="fullWidth"
          >
            <Tab label="会計明細" />
            <Tab label="注文履歴" />
          </Tabs>
        </Box>

        <TabPanel value={tabIndex} index={0}>
          {renderBillItems()}
        </TabPanel>
        <TabPanel value={tabIndex} index={1}>
          {renderCartItems()}
        </TabPanel>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          color="inherit"
        >
          キャンセル
        </Button>
        {billData.byBillItems.items.length > -1 ? (
          <Button
            variant="contained"
            onClick={handleCheckoutOrder}
            color="primary"
          >
            会計確定
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleCloseDiningTable}
            color="error"
          >
            テーブルクローズ
          </Button>
        )}
      </DialogActions>
      <ConfirmDialog
        open={checkoutDialogOpen}
        onClose={() => setCheckoutDialogOpen(false)}
        onConfirm={handleConfirmCheckout}
        title="注文確定"
        message="この注文を確定しますか？"
        confirmText="確定"
        cancelText="キャンセル"
        confirmColor="success"
      />
      <ConfirmDialog
        open={closeTableDialogOpen}
        onClose={() => setCloseTableDialogOpen(false)}
        onConfirm={handleConfirmCloseTable}
        title="テーブルクローズ"
        message="このテーブルをクローズしますか？"
        confirmText="クローズ"
        cancelText="キャンセル"
        confirmColor="error"
      />
    </Dialog>
  );
};

export default BillConfirmModal;